import React, { Fragment, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaSpinner, FaBox, FaBoxes, FaEdit, FaTrash, FaEye, FaStar, FaSortUp, FaSortDown, FaSort, FaUndo } from "react-icons/fa";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function ProductsPage() {
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [parentCategories, setParentCategories] = useState([]);
        // Fetch parent categories
        const getParentCategories = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/parent-categories`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setParentCategories(response.data);
            } catch (error) {
                handleApiError(error);
            }
        };
    const [showAddModal, setShowAddModal] = useState(false);
    const [brandSearchTerm, setBrandSearchTerm] = useState('');
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const [parentCategorySearchTerm, setParentCategorySearchTerm] = useState('');
    const [showParentCategoryDropdown, setShowParentCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showUnitDropdown, setShowUnitDropdown] = useState(false);
    const [showStockModal, setShowStockModal] = useState(false);
    const [selectedProductForStock, setSelectedProductForStock] = useState(null);
    const [addedStockValue, setAddedStockValue] = useState('');
    const [showBulkStockModal, setShowBulkStockModal] = useState(false);
    const [bulkFilterType, setBulkFilterType] = useState('all');
    const [bulkFilterValue, setBulkFilterValue] = useState('');
    const [bulkAction, setBulkAction] = useState('set');
    const [bulkStockValue, setBulkStockValue] = useState('');
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [productData, setProductData] = useState({ rating: 0 });
    const [productList, setProductList] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [uploadInProgress, setUploadInProgress] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 100,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [imageUploads, setImageUploads] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false,
        thumbnail: false,
        video: false,
    });

    const filteredBrandsForSelect = brands.filter(b =>
        (b.name || '').toLowerCase().includes(brandSearchTerm.toLowerCase())
    );

    const filteredParentCategoriesForSelect = parentCategories.filter(pc =>
        (pc.name || '').toLowerCase().includes(parentCategorySearchTerm.toLowerCase())
    );

    const filteredCategoriesForSelect = categories.filter(c =>
        (c.name || '').toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const standardUnits = ['kg', 'g', 'pcs', 'pc', 'packet', 'packets', 'box', 'ml', 'l', 'bottle', 'pack', 'bag', 'pouch', 'dozen', 'piece', 'pieces', 'bundle', 'roll', 'set', 'carton', 'crate', 'can'];
    const filteredUnitsForSelect = standardUnits.filter(u =>
        u.toLowerCase().includes((productData.unit || '').toLowerCase())
    );

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getProductData = async (page = 1, search = '', sort = '', append = false) => {
        if (page === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const params = new URLSearchParams({
                paginate: 'true',
                page: page.toString(),
                limit: '100',
                search: search,
                sortBy: sort
            });
            const response = await axios.get(`${API_BASE_URL}/product-list?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const newProducts = response.data.products || [];
            if (append) {
                setProductList(prev => [...prev, ...newProducts]);
                setFilteredProducts(prev => [...prev, ...newProducts]);
            } else {
                setProductList(newProducts);
                setFilteredProducts(newProducts);
            }
            setPagination(response.data.pagination || {
                currentPage: page,
                totalPages: 1,
                totalItems: newProducts.length,
                itemsPerPage: 100,
                hasNextPage: false,
                hasPrevPage: false
            });
            setCurrentPage(page);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const getCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categorydata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getBrands = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/branddata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    // Debounced search function
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    getProductData(1, value, sortBy, false);
                }, 500); // 500ms delay
            };
        })(),
        [sortBy]
    );

    // Search products with debouncing
    const handleSearchProduct = (event) => {
        const search = event.target.value;
        setSearchTerm(search);
        debouncedSearch(search);
    };

    // Handle sort change
    const handleSortChange = (event) => {
        const value = event.target.value;
        setSortBy(value);
        getProductData(1, searchTerm, value, false);
    };

    // Reset filters
    const handleReset = () => {
        setSearchTerm('');
        setSortBy('');
        getProductData(1, '', '', false);
    };

    const handleChange = (event) => {
        setProductData({
            ...productData,
            [event.target.name]: event.target.value
        });
    };

    // Custom star rating handler
    const handleStarClick = (starValue) => {
        setProductData((prevData) => ({
            ...prevData,
            rating: starValue
        }));
    };

    const handleImageUpload = async (event) => {
        const { name } = event.target;
        const file = event.target.files[0];

        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, WebP, or AVIF images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        setUploadInProgress((prev) => prev + 1);
        try {
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProductData((prevData) => ({
                ...prevData,
                [name]: response.data.path
            }));
            setImageUploads((prevUploads) => ({
                ...prevUploads,
                [name]: true
            }));
            alert("Image uploaded successfully!");
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadInProgress((prev) => prev - 1);
        }
    };

    const handleVideoUpload = async (event) => {
        const { name } = event.target;
        const file = event.target.files[0];

        if (!file) return;

        const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only MP4, WebM, or OGG videos are allowed.');
            return;
        }

        if (file.size > 50 * 1024 * 1024) { // 50MB limit
            alert('Video size must be under 50MB.');
            return;
        }

        const formData = new FormData();
        formData.append('video', file);

        setUploadInProgress((prev) => prev + 1);
        try {
            const response = await axios.post(`${API_BASE_URL}/upload-video`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProductData((prevData) => ({
                ...prevData,
                [name]: response.data.path
            }));
            setImageUploads((prevUploads) => ({
                ...prevUploads,
                [name]: true
            }));
            alert('Video uploaded successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadInProgress((prev) => prev - 1);
        }
    };

    const validateFields = () => {
        const requiredFields = ['name', 'brand', 'category', 'originalPrice', 'price', 'quantity', 'unit', 'availableStock', 'description', 'image1', 'image2', 'thumbnail'];
        for (let field of requiredFields) {
            if (!productData[field] || productData[field].toString().trim() === '') {
                alert(`${field} is required.`);
                return false;
            }
        }
        // Require a star rating (must be 1-5)
        if (!productData.rating || productData.rating < 1 || productData.rating > 5) {
            alert('Please select a star rating for the product.');
            return false;
        }
        if (Number(productData.price) > Number(productData.originalPrice)) {
            alert('Price cannot be greater than Original Price.');
            return false;
        }
        // Stock must be a non-negative number
        if (Number(productData.availableStock) < 0 || Number.isNaN(Number(productData.availableStock))) {
            alert('Stock must be a non-negative number.');
            return false;
        }
        // Only require image1, image2, and thumbnail to be uploaded
        if (!imageUploads.image1 || !imageUploads.image2 || !imageUploads.thumbnail) {
            alert('Image 1, Image 2, and Thumbnail must be uploaded before saving.');
            return false;
        }
        return true;
    };

    const handleSaveData = async () => {
        if (!validateFields()) return;

        // Prepare product data, process tags as array
        let dataToSave = { ...productData };
        if (typeof dataToSave.tags === 'string') {
            dataToSave.tags = dataToSave.tags
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0);
        }

        setUploadInProgress((prev) => prev + 1);
        try {
            const response = await axios.post(`${API_BASE_URL}/save-product-data`, dataToSave, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);

            setProductData({});
            setImageUploads({ image1: false, image2: false, image3: false, image4: false, thumbnail: false, video: false });
            getProductData(1, searchTerm, sortBy, false);
            setShowAddModal(false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadInProgress((prev) => prev - 1);
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/delete-product/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            getProductData(1, searchTerm, sortBy, false);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleOpenStockModal = (product) => {
        setSelectedProductForStock(product);
        setAddedStockValue('');
        setShowStockModal(true);
    };

    const handleUpdateStock = async () => {
        const valueToAdd = Number(addedStockValue || 0);
        if (valueToAdd < 0 || Number.isNaN(valueToAdd)) {
            alert('Stock to add must be a non-negative number.');
            return;
        }
        
        const currentStock = selectedProductForStock?.availableStock || 0;
        const newTotalStock = currentStock + valueToAdd;

        setUploadInProgress((prev) => prev + 1);
        try {
            const response = await axios.patch(`${API_BASE_URL}/update-product`, {
                _id: selectedProductForStock._id,
                availableStock: newTotalStock
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message || 'Stock updated successfully!');
            setShowStockModal(false);
            getProductData(1, searchTerm, sortBy, false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadInProgress((prev) => prev - 1);
        }
    };

    const handleBulkModifyStock = async () => {
        const qty = parseInt(bulkStockValue, 10);
        if (isNaN(qty) || qty < 0) {
            alert('Please enter a valid non-negative stock quantity.');
            return;
        }

        if (bulkFilterType !== 'all' && !bulkFilterValue) {
            alert(`Please select a ${bulkFilterType} from the list.`);
            return;
        }

        const confirmMsg = `Are you sure you want to ${bulkAction} stock by ${qty} units for ${
            bulkFilterType === 'all' 
                ? 'ALL products' 
                : `${bulkFilterType} "${bulkFilterValue}"`
        }?`;
        
        if (!window.confirm(confirmMsg)) return;

        setBulkUpdating(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/products/bulk-modify-stock`, {
                filterType: bulkFilterType,
                filterValue: bulkFilterValue,
                action: bulkAction,
                value: qty
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert(`${response.data.message}\nMatched: ${response.data.matchedCount} products.\nModified: ${response.data.modifiedCount} products.`);
            setShowBulkStockModal(false);
            // Reset
            setBulkStockValue('');
            setBulkFilterValue('');
            setBulkFilterType('all');
            setBulkAction('set');
            getProductData(1, searchTerm, sortBy, false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setBulkUpdating(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    useEffect(() => {
        getCategories();
        getProductData(1, '', '', false);
        getBrands();
        getParentCategories();
    }, []);

    // Scroll listener for infinite scroll
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 150) {
                if (!loading && !loadingMore && pagination.hasNextPage) {
                    getProductData(currentPage + 1, searchTerm, sortBy, true);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, loadingMore, currentPage, pagination.hasNextPage, searchTerm, sortBy]);

    const isSaveDisabled = uploadInProgress > 0;

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">
                                    PRODUCT MANAGEMENT ({pagination.totalItems} TOTAL)
                                </h4>
                                <p className="text-muted p-0 m-0">Manage your product catalog and inventory</p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button 
                                    type="button" 
                                    className="btn btn-outline-primary btn-sm rounded-0"
                                    style={{
                                        padding: '6px 16px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        height: '34px',
                                        border: '1px solid #1a237e',
                                        color: '#1a237e'
                                    }}
                                    onClick={() => setShowBulkStockModal(true)}
                                >
                                    <FaBoxes size={13} />
                                    Bulk Edit Stock
                                </button>
                                <button 
                                    type="button" 
                                    className="btn theme-btn-blue-flat btn-sm rounded-0"
                                    style={{
                                        padding: '6px 16px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        height: '34px'
                                    }}
                                    onClick={() => setShowAddModal(true)}
                                >
                                    <FaPlus size={11} />
                                    Add Product
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Control Panel — cloned from Brands page */}
                <div className="control-panel-bar">
                    <div className="control-panel-search">
                        <span
                            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
                            style={{ pointerEvents: 'none' }}
                        >
                            <FaSearch style={{ fontSize: '14px' }} />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-5"
                            placeholder="Search by product name, brand, or category..."
                            style={{
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                height: '38px',
                                fontSize: '14px',
                                background: '#ffffff',
                                width: '100%'
                            }}
                            value={searchTerm}
                            onChange={handleSearchProduct}
                        />
                    </div>
                    <div className="control-panel-sort" style={{ minWidth: '160px' }}>
                        <select
                            className="form-select form-select-sm"
                            style={{
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                height: '38px',
                                fontSize: '14px',
                                color: '#4b5563',
                                fontWeight: 500,
                                background: '#ffffff',
                                width: '100%'
                            }}
                            value={sortBy}
                            onChange={handleSortChange}
                        >
                            <option value="">Default Sort</option>
                            <option value="name_asc">Name (A - Z)</option>
                            <option value="name_desc">Name (Z - A)</option>
                            <option value="price_asc">Price (Low to High)</option>
                            <option value="price_desc">Price (High to Low)</option>
                            <option value="stock_asc">Stock (Low to High)</option>
                            <option value="stock_desc">Stock (High to Low)</option>
                        </select>
                    </div>
                    <div className="control-panel-reset">
                        <button
                            className="btn-reset-square"
                            title="Reset Filters"
                            onClick={handleReset}
                        >
                            <FaUndo style={{ fontSize: '14px' }} />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <AdminPageLoader message="Loading products..." />
                ) : (
                    <div className="row orders-table-row">
                        <div className="col-12">
                            {filteredProducts.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover orders-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '35%', minWidth: '220px' }}>Product Info</th>
                                                <th style={{ width: '25%', minWidth: '180px' }}>Categories & Brand</th>
                                                <th style={{ width: '15%', minWidth: '110px' }}>Pricing</th>
                                                <th style={{ width: '15%', minWidth: '130px' }}>
                                                    Inventory & Stock
                                                </th>
                                                <th style={{ width: '10%', minWidth: '90px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredProducts.map((product, index) => (
                                                <tr key={product._id}>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div style={{ width: '40px', height: '40px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                                <img 
                                                                    src={`${API_BASE_URL}/${product.thumbnail}`} 
                                                                    alt={product.name} 
                                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                                    onError={(e) => {
                                                                        e.target.src = 'https://via.placeholder.com/40?text=P';
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="d-flex flex-column align-items-start">
                                                                <strong style={{ color: '#1a237e', fontSize: '14px' }}>{product.name}</strong>
                                                                <span className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                                    Sr. #{index + 1}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-1 align-items-start">
                                                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                                                <span className="text-muted" style={{ fontWeight: '500' }}>Parent:</span>
                                                                <span style={{ fontWeight: '600', color: '#1a237e' }}>{product.parentCategory || 'N/A'}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                                                <span className="text-muted" style={{ fontWeight: '500' }}>Category:</span>
                                                                <span style={{ fontWeight: '600', color: '#0f172a' }}>{product.category || 'N/A'}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                                                <span className="text-muted" style={{ fontWeight: '500' }}>Brand:</span>
                                                                <span style={{ fontWeight: '600', color: '#92400e' }}>{product.brand || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column align-items-start">
                                                            <span style={{ fontWeight: '700', color: '#16a34a', fontSize: '15px' }}>
                                                                {formatCurrency(product.price)}
                                                            </span>
                                                            <span className="text-muted" style={{ fontSize: '11px', textDecoration: 'line-through', marginTop: '1px' }}>
                                                                MRP: {formatCurrency(product.originalPrice)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column align-items-start">
                                                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                                                <span className="text-muted" style={{ fontWeight: '500' }}>Size:</span>
                                                                <span className="fw-semibold text-dark">{product.quantity} {product.unit}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: '12px' }}>
                                                                <span className="text-muted" style={{ fontWeight: '500' }}>Stock:</span>
                                                                <span className={`fw-bold ${product.availableStock > 0 ? 'text-success' : 'text-danger'}`}>
                                                                    {product.availableStock || 0} units
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Link 
                                                                to={`/admin/products/edit/${product._id}`} 
                                                                className="btn-action-square btn-action-view"
                                                                title="Edit Product"
                                                            >
                                                                <FaEdit size={13} />
                                                            </Link>
                                                            <button
                                                                className="btn-action-square btn-action-view"
                                                                style={{ background: '#e0e7ff', color: '#4f46e5' }}
                                                                onClick={() => handleOpenStockModal(product)}
                                                                title="Update Stock"
                                                            >
                                                                <FaBox size={12} />
                                                            </button>
                                                            <button
                                                                className="btn-action-square btn-action-cancel"
                                                                onClick={() => handleDeleteProduct(product._id)}
                                                                title="Delete Product"
                                                            >
                                                                <FaTrash size={12} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Infinite Scroll loading indicators */}
                                    {loadingMore && (
                                        <div className="text-center py-4 bg-white border-bottom">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                <span className="visually-hidden">Loading more...</span>
                                            </div>
                                            <span className="ms-2 text-muted" style={{ fontSize: '14px', fontWeight: 500 }}>Loading more products...</span>
                                        </div>
                                    )}

                                    {!pagination.hasNextPage && filteredProducts.length > 0 && (
                                        <div className="text-center py-4 bg-white border-bottom text-muted" style={{ fontSize: '13px', fontWeight: 500 }}>
                                            Showing all {pagination.totalItems} products.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaBox className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No products found matching your search.' : 'No products found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first product to get started.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {/* Add Product Modal — assign-driver / brand modal style */}
            {showAddModal && (
                <div className="assign-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="assign-modal-content" style={{ width: '760px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaBox />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        Add New Product
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                        Fill in the details to add a new product to your inventory
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div className="assign-modal-body" style={{ overflowY: 'auto', flex: '1 1 auto', padding: '20px' }}>
                            <div className="row g-3">
                                {/* Row 1 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Product Name</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        placeholder="Enter product name" 
                                        name="name" 
                                        value={productData.name || ''} 
                                        onChange={handleChange} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Brand</label>
                                    <div className="position-relative">
                                        <input 
                                            type="text"
                                            className="form-control"
                                            style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                            placeholder="Search & select brand..."
                                            value={showBrandDropdown ? brandSearchTerm : (productData.brand || '')}
                                            onFocus={() => {
                                                setBrandSearchTerm('');
                                                setShowBrandDropdown(true);
                                            }}
                                            onChange={(e) => setBrandSearchTerm(e.target.value)}
                                            onBlur={() => setTimeout(() => setShowBrandDropdown(false), 250)}
                                        />
                                        {showBrandDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                background: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                zIndex: 20000,
                                                marginTop: '4px'
                                            }}>
                                                {filteredBrandsForSelect.length > 0 ? (
                                                    filteredBrandsForSelect.map((brand) => (
                                                        <div
                                                            key={brand._id}
                                                            style={{
                                                                padding: '8px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                color: '#334155',
                                                                transition: 'background 0.15s'
                                                            }}
                                                            onMouseDown={() => {
                                                                setProductData(prev => ({ ...prev, brand: brand.name }));
                                                                setBrandSearchTerm(brand.name);
                                                                setShowBrandDropdown(false);
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            {brand.name}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                        No brands found
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Row 2 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Parent Category</label>
                                    <div className="position-relative">
                                        <input 
                                            type="text"
                                            className="form-control"
                                            style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                            placeholder="Search & select parent category..."
                                            value={showParentCategoryDropdown ? parentCategorySearchTerm : (productData.parentCategory || '')}
                                            onFocus={() => {
                                                setParentCategorySearchTerm('');
                                                setShowParentCategoryDropdown(true);
                                            }}
                                            onChange={(e) => setParentCategorySearchTerm(e.target.value)}
                                            onBlur={() => setTimeout(() => setShowParentCategoryDropdown(false), 250)}
                                        />
                                        {showParentCategoryDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                background: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                zIndex: 20000,
                                                marginTop: '4px'
                                            }}>
                                                {filteredParentCategoriesForSelect.length > 0 ? (
                                                    filteredParentCategoriesForSelect.map((parent) => (
                                                        <div
                                                            key={parent._id}
                                                            style={{
                                                                padding: '8px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                color: '#334155',
                                                                transition: 'background 0.15s'
                                                            }}
                                                            onMouseDown={() => {
                                                                setProductData(prev => ({ ...prev, parentCategory: parent.name }));
                                                                setParentCategorySearchTerm(parent.name);
                                                                setShowParentCategoryDropdown(false);
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            {parent.name}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                        No parent categories found
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Category</label>
                                    <div className="position-relative">
                                        <input 
                                            type="text"
                                            className="form-control"
                                            style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                            placeholder="Search & select category..."
                                            value={showCategoryDropdown ? categorySearchTerm : (productData.category || '')}
                                            onFocus={() => {
                                                setCategorySearchTerm('');
                                                setShowCategoryDropdown(true);
                                            }}
                                            onChange={(e) => setCategorySearchTerm(e.target.value)}
                                            onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 250)}
                                        />
                                        {showCategoryDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                background: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                zIndex: 20000,
                                                marginTop: '4px'
                                            }}>
                                                {filteredCategoriesForSelect.length > 0 ? (
                                                    filteredCategoriesForSelect.map((category) => (
                                                        <div
                                                            key={category._id}
                                                            style={{
                                                                padding: '8px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                color: '#334155',
                                                                transition: 'background 0.15s'
                                                            }}
                                                            onMouseDown={() => {
                                                                setProductData(prev => ({ ...prev, category: category.name }));
                                                                setCategorySearchTerm(category.name);
                                                                setShowCategoryDropdown(false);
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            {category.name}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                        No categories found
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Row 3 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Quantity</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        placeholder="Enter quantity (e.g. 500)" 
                                        name="quantity" 
                                        value={productData.quantity || ''} 
                                        onChange={handleChange} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Unit</label>
                                    <div className="position-relative">
                                        <input 
                                            type="text"
                                            className="form-control"
                                            style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                            placeholder="Search, select, or type unit..."
                                            value={productData.unit || ''}
                                            onFocus={() => setShowUnitDropdown(true)}
                                            onChange={(e) => setProductData(prev => ({ ...prev, unit: e.target.value }))}
                                            onBlur={() => setTimeout(() => setShowUnitDropdown(false), 250)}
                                        />
                                        {showUnitDropdown && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                background: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                zIndex: 20000,
                                                marginTop: '4px'
                                            }}>
                                                {filteredUnitsForSelect.length > 0 ? (
                                                    filteredUnitsForSelect.map((unitOption, idx) => (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                padding: '8px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                color: '#334155',
                                                                transition: 'background 0.15s'
                                                            }}
                                                            onMouseDown={() => {
                                                                setProductData(prev => ({ ...prev, unit: unitOption }));
                                                                setShowUnitDropdown(false);
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            {unitOption}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                        Press Tab or click away to keep custom unit: "{productData.unit}"
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Row 4 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>MRP Price</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        placeholder="Enter MRP price" 
                                        min={0} 
                                        name="originalPrice" 
                                        value={productData.originalPrice || ''} 
                                        onChange={handleChange} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Sales Price</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        placeholder="Enter sales price" 
                                        min={0} 
                                        name="price" 
                                        value={productData.price || ''} 
                                        onChange={handleChange} 
                                    />
                                </div>

                                {/* Row 5 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Stock</label>
                                    <input
                                        type="number"
                                        min={0}
                                        className="form-control"
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        placeholder="Enter available stock"
                                        name="availableStock"
                                        value={productData.availableStock ?? ''}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Star Rating</label>
                                    <div className="d-flex align-items-center" style={{ height: '38px' }}>
                                        {[1,2,3,4,5].map((star) => (
                                            <FaStar
                                                key={star}
                                                size={22}
                                                style={{ cursor: 'pointer', color: star <= (productData.rating || 0) ? '#ffd700' : '#e4e5e9', marginRight: 4 }}
                                                onClick={() => handleStarClick(star)}
                                                title={star + ' Star'}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Row 6 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Tags</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        placeholder="Enter tags, separated by commas (e.g. organic, vegan)"
                                        name="tags"
                                        value={productData.tags || ''}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Product Video (Optional)</label>
                                    <input
                                        className="form-control"
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                        type="file"
                                        accept="video/*"
                                        name="video"
                                        onChange={handleVideoUpload}
                                    />
                                    {productData.video && (
                                        <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>✓ Video uploaded</small>
                                    )}
                                </div>

                                {/* Row 7 */}
                                <div className="col-12">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Description</label>
                                    <textarea 
                                        rows={2} 
                                        className="form-control"
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '62px', resize: 'none' }}
                                        name="description" 
                                        value={productData.description || ''} 
                                        onChange={handleChange} 
                                        placeholder="Enter product description"
                                    ></textarea>
                                </div>

                                {/* Row 8 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Thumbnail Image (Required)</label>
                                    <input 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                        type="file" 
                                        accept="image/*" 
                                        name="thumbnail" 
                                        onChange={handleImageUpload} 
                                    />
                                    {imageUploads.thumbnail && (
                                        <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>✓ Thumbnail uploaded</small>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Cover Image 1 (Required)</label>
                                    <input 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                        type="file" 
                                        accept="image/*" 
                                        name="image1" 
                                        onChange={handleImageUpload} 
                                    />
                                    {imageUploads.image1 && (
                                        <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>✓ Cover Image 1 uploaded</small>
                                    )}
                                </div>

                                {/* Row 9 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Cover Image 2 (Required)</label>
                                    <input 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                        type="file" 
                                        accept="image/*" 
                                        name="image2" 
                                        onChange={handleImageUpload} 
                                    />
                                    {imageUploads.image2 && (
                                        <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>✓ Cover Image 2 uploaded</small>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Cover Image 3 (Optional)</label>
                                    <input 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                        type="file" 
                                        accept="image/*" 
                                        name="image3" 
                                        onChange={handleImageUpload} 
                                    />
                                    {imageUploads.image3 && (
                                        <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>✓ Cover Image 3 uploaded</small>
                                    )}
                                </div>

                                {/* Row 10 */}
                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Cover Image 4 (Optional)</label>
                                    <input 
                                        className="form-control" 
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                        type="file" 
                                        accept="image/*" 
                                        name="image4" 
                                        onChange={handleImageUpload} 
                                    />
                                    {imageUploads.image4 && (
                                        <small className="text-success d-block mt-1" style={{ fontSize: '11px' }}>✓ Cover Image 4 uploaded</small>
                                    )}
                                </div>
                            </div>
                            {uploadInProgress > 0 && (
                                <div className="mt-3 d-flex align-items-center gap-2">
                                    <FaSpinner className="spinner-border spinner-border-sm text-primary" role="status" style={{ border: 'none', background: 'none' }} />
                                    <small className="text-muted">Uploading media files ({uploadInProgress} remaining)...</small>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="assign-modal-footer">
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: '1px solid #e2e8f0', background: '#ffffff',
                                    color: '#374151', fontSize: '13px', fontWeight: '500',
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveData}
                                disabled={isSaveDisabled}
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: 'none', background: isSaveDisabled ? '#c7d2fe' : '#1a237e',
                                    color: isSaveDisabled ? '#6366f1' : '#ffff01',
                                    fontSize: '13px', fontWeight: '600',
                                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px'
                                }}
                            >
                                {uploadInProgress > 0 ? (
                                    <><FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Saving...</>
                                ) : 'Save Product'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Quick Stock Update Modal */}
            {showStockModal && (
                <div className="assign-modal-overlay" onClick={() => setShowStockModal(false)}>
                    <div className="assign-modal-content" style={{ width: '420px', maxWidth: '95vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        
                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaBox />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        Update Product Stock
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px', textAlign: 'left' }}>
                                        {selectedProductForStock?.name}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowStockModal(false)}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '20px' }}>
                            <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '6px', border: '1px solid #f1f5f9', marginBottom: '16px', textAlign: 'left' }}>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>Current Inventory</div>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                                    {selectedProductForStock?.availableStock || 0} units
                                </div>
                            </div>
                            <div className="mb-3" style={{ textAlign: 'left' }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                    Stock Quantity to Add
                                </label>
                                <input 
                                    type="number"
                                    className="form-control"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    placeholder="e.g. 100"
                                    value={addedStockValue}
                                    onChange={(e) => setAddedStockValue(e.target.value)}
                                    min={0}
                                />
                            </div>
                            {addedStockValue && !Number.isNaN(Number(addedStockValue)) && Number(addedStockValue) > 0 && (
                                <div style={{ background: '#f0fdf4', padding: '10px 14px', borderRadius: '6px', border: '1px solid #dcfce7', textAlign: 'left', fontSize: '12.5px', color: '#166534', fontWeight: '500' }}>
                                    New Inventory Total: {selectedProductForStock?.availableStock || 0} + {addedStockValue} = <strong style={{ fontSize: '13.5px' }}>{(selectedProductForStock?.availableStock || 0) + Number(addedStockValue)}</strong> units
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: '#f8fafc' }}>
                            <button 
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ height: '36px', fontSize: '13px', borderRadius: '6px', padding: '0 16px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#374151' }}
                                onClick={() => setShowStockModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button"
                                className="btn btn-primary btn-sm"
                                style={{ height: '36px', fontSize: '13px', borderRadius: '6px', padding: '0 16px', background: 'linear-gradient(135deg, #1a237e 0%, #311b92 100%)', border: 'none', color: '#ffffff', fontWeight: '600' }}
                                onClick={handleUpdateStock}
                            >
                                Update Stock
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Stock Update Modal */}
            {showBulkStockModal && (
                <div className="assign-modal-overlay" onClick={() => setShowBulkStockModal(false)}>
                    <div className="assign-modal-content" style={{ width: '460px', maxWidth: '95vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        
                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaBoxes />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        Bulk Edit Stock
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px', textAlign: 'left' }}>
                                        Modify stock values for multiple products at once
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowBulkStockModal(false)}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '20px', textAlign: 'left' }}>
                            {/* 1. Target Segment Selector */}
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                    1. Target Products Segment
                                </label>
                                <select 
                                    className="form-select"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    value={bulkFilterType}
                                    onChange={(e) => {
                                        setBulkFilterType(e.target.value);
                                        setBulkFilterValue('');
                                    }}
                                >
                                    <option value="all">All Products (Full Catalog)</option>
                                    <option value="brand">Filter by Brand</option>
                                    <option value="category">Filter by Category</option>
                                    <option value="parentCategory">Filter by Parent Category</option>
                                </select>
                            </div>

                            {/* 2. Target Value Selector (conditional dropdown) */}
                            {bulkFilterType !== 'all' && (
                                <div className="mb-3">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                        2. Select {bulkFilterType === 'parentCategory' ? 'Parent Category' : bulkFilterType.charAt(0).toUpperCase() + bulkFilterType.slice(1)}
                                    </label>
                                    <select 
                                        className="form-select"
                                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                        value={bulkFilterValue}
                                        onChange={(e) => setBulkFilterValue(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Choose one --</option>
                                        {bulkFilterType === 'brand' && brands.map(b => (
                                            <option key={b._id} value={b.name}>{b.name}</option>
                                        ))}
                                        {bulkFilterType === 'category' && categories.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                        {bulkFilterType === 'parentCategory' && parentCategories.map(pc => (
                                            <option key={pc._id} value={pc.name}>{pc.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* 3. Action Selector */}
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                    {bulkFilterType !== 'all' ? '3. Stock Action' : '2. Stock Action'}
                                </label>
                                <select 
                                    className="form-select"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    value={bulkAction}
                                    onChange={(e) => setBulkAction(e.target.value)}
                                >
                                    <option value="set">Set Stock To (Overwrite)</option>
                                    <option value="add">Add Stock Quantity (+)</option>
                                    <option value="subtract">Subtract Stock Quantity (-)</option>
                                </select>
                            </div>

                            {/* 4. Stock Quantity Value */}
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                                    {bulkFilterType !== 'all' ? '4. Stock Quantity (Units)' : '3. Stock Quantity (Units)'}
                                </label>
                                <input 
                                    type="number"
                                    className="form-control"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    placeholder="e.g. 100"
                                    value={bulkStockValue}
                                    onChange={(e) => setBulkStockValue(e.target.value)}
                                    min={0}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: '#f8fafc' }}>
                            <button 
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ height: '36px', fontSize: '13px', borderRadius: '6px', padding: '0 16px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#374151' }}
                                onClick={() => setShowBulkStockModal(false)}
                                disabled={bulkUpdating}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button"
                                className="btn btn-primary btn-sm"
                                style={{ height: '36px', fontSize: '13px', borderRadius: '6px', padding: '0 16px', background: 'linear-gradient(135deg, #1a237e 0%, #311b92 100%)', border: 'none', color: '#ffffff', fontWeight: '600' }}
                                onClick={handleBulkModifyStock}
                                disabled={bulkUpdating}
                            >
                                {bulkUpdating ? 'Applying...' : 'Apply Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default ProductsPage;
